import { ShopCodeEnum } from '../config/config.schema';
import configService from '../config/config.service';
import {
  ADS,
  BACKLIT_CODE,
  BACKLIT_VALUE_CODES,
  BUNDLE_PRODUCT_TEXTURES,
  PATTERN_SETS,
  PRINTED_SIDE_CODE,
  PRINTED_SIDE_VALUE_CODES,
  S_CO_BRANDING,
  S_QTY_CODE,
  SIZE_CODE,
  SWAG_PRODUCT_CODE,
  SWAG_PRODUCT_VALUE_CODE,
  TENT_ADS,
} from './constants';
import {
  Location,
  Product,
  ProductSpec,
  SpecValue,
  TModel,
  TBundle,
  Surface,
  Section,
  PATTERN_SET_COLORS,
} from './hooks/bundle-store';
import * as THREE from 'three';
import useThemeStore from './hooks/use-theme-store';

export const initBundleProducts = async ({
  bundleProducts,
  calcState,
  isApparel,
  bundle,
  shopCode,
}: any) => {
  let items = bundleProducts;
  const prevVersion = bundleProducts.every((item: any) => !item?.itemParentId);

  if (!prevVersion) {
    items = bundleProducts
      ?.filter((item: any) => !item?.itemParentId)
      .concat(bundleProducts.filter((item: any) => item?.itemParentId));
  }

  const finalData = await items?.reduce(
    async (
      accPromise: {
        models: TModel[];
        floorWidth: number;
        floorLength: number;
        surfaceIds: string[];
        itemsHaveBacklit: string[];
      },
      item: any,
      index: number,
    ) => {
      const acc = await accPromise;

      const patternSetSurfaces = getPatternSetSurfaces(bundle?.patternSet?.items);

      const colorTheme = bundle?.patternSet?.colorTheme;

      //init specs
      const specs = await initSpecs({
        item: { ...item },
        calcState,
        isApparel,
        colorTheme,
        patternSetSurfaces,
      });

      const visible = calcState?.products?.[item?.id]?.selected;

      const newSection = {
        id: item.id,
        visible,
        product: {
          ...item?.product,
          specs,
        },
      };

      const itemModel = item?.model ? item?.model : item?.product?.model;

      const sectionModelId = itemModel?.id;
      const modelId = index + 1;
      const itemParentId = item?.itemParentId;

      if (!sectionModelId) {
        const model = acc?.models?.find((model: TModel) => {
          return model?.code === 'WITHOUT_SECTION';
        });
        if (model) {
          model.sections = [
            ...model.sections,
            {
              ...newSection,
              modelId: model?.id,
            },
          ];
          return {
            ...acc,
          };
        } else {
          const newModel = {
            code: 'WITHOUT_SECTION',
            id: modelId,
            sections: [
              {
                ...newSection,
                modelId,
              },
            ],
          };
          return {
            ...acc,
            models: [...acc.models, newModel],
          };
        }
      }

      const sizeSpec = specs?.find((s) => s.code === SIZE_CODE);
      const section: Section = item?.modelSection
        ? {
            ...item?.modelSection,
            itemId: item?.id + '_2ND_' + item?.modelSection?.id,
          }
        : sizeSpec
          ? sizeSpec?.specValue?.section
          : {
              ...item?.product?.modelSection,
              itemId: item?.id + '_2ND_' + item?.product?.modelSection?.id,
            };

      const id = section?.itemId as string;

      //manage backlit spec
      const backlitSpec = specs?.find((s) => s.code === BACKLIT_CODE);
      const isBacklit = backlitSpec?.specValue?.code === BACKLIT_VALUE_CODES[0];
      const hasBacklitSpec = Boolean(backlitSpec);
      if (isBacklit) {
        acc.itemsHaveBacklit.push(id);
      }

      //manage printed side spec
      let isSingleSide = false;
      let isOutAsSingleSide = false;
      if (section?.surfaces?.length === 2) {
        const insideKey = PRINTED_SIDE_VALUE_CODES[0];
        const outsideKey = PRINTED_SIDE_VALUE_CODES[1];

        const printedSideSpec = specs?.find((s) => s.code === PRINTED_SIDE_CODE);
        const code = printedSideSpec?.specValue?.code;

        isSingleSide = code === insideKey || code === outsideKey;
        isOutAsSingleSide = code === outsideKey;
      }

      const result = item?.modelSection
        ? await initSurfaces(
            item?.id,
            item?.modelSection?.surfaces,
            isSingleSide,
            isOutAsSingleSide,
            shopCode,
            undefined,
            isApparel,
            colorTheme,
            patternSetSurfaces,
          )
        : sizeSpec
          ? {
              surfaceIds: isSingleSide
                ? [section?.surfaceIds?.[isOutAsSingleSide ? 1 : 0]]
                : section?.surfaceIds,
              surfaces: section?.surfaces,
            }
          : await initSurfaces(
              item?.id,
              item?.product?.modelSection?.surfaces,
              isSingleSide,
              isOutAsSingleSide,
              shopCode,
              undefined,
              isApparel,
              colorTheme,
              patternSetSurfaces,
            );

      const surfaceIds = result?.surfaceIds ?? [];
      const surfaces = result?.surfaces ?? [];

      const surfaceIdsInstance = { surfaceIds: [...acc.surfaceIds, ...surfaceIds] };
      const isMain = section?.isMain;
      const isSemitransparent = section?.isSemitransparent;
      const sectionCode = section?.code;
      const productHaveMainSection = item?.product?.modelSection?.isMain;

      //manage co-branding spec
      let sizeBrandTextures;
      const coBrandingSpec = specs?.find((s) => s.code === S_CO_BRANDING);
      const productReferral = item?.product?.productReferral;
      const coBrandingSvg = productReferral?.coBrandingSvg;
      const location = productReferral?.location;
      const size = productReferral?.size;

      if (coBrandingSpec && coBrandingSvg && sizeSpec) {
        const url =
          configService.getBaseUrl(shopCode).s3BucketStorage + '/' + ADS + '/' + coBrandingSvg;
        sizeBrandTextures = Object.fromEntries(
          await Promise.all(
            sizeSpec?.specsValue?.map(async (sv) => {
              const surfacesTextures = await Promise.all(
                sv?.section?.surfaces?.map(async (surface) => {
                  try {
                    if (surface?.url) {
                      return await createTextureWithSvg({
                        jpgUrl: surface.url,
                        svgUrl: url,
                        outWidth: changeInchToPX(surface.width),
                        outHeight: changeInchToPX(surface.height),
                        svgX: changeInchToPX(location?.left),
                        svgY: changeInchToPX(location?.top),
                        svgWidth: changeInchToPX(size?.width),
                        svgHeight: changeInchToPX(size?.height),
                        svgRotation: location?.rotation ?? 0,
                      });
                    }

                    return await loadTexture(url + '?t=' + Date.now());
                  } catch (error) {
                    console.error('Error loading brand advertising', error);
                    return null;
                  }
                }) ?? [],
              );

              return [sv.code, surfacesTextures.filter(Boolean)];
            }) ?? [],
          ),
        );
      }
      const brandTextures = sizeBrandTextures
        ? sizeBrandTextures[sizeSpec?.specValue?.code || '']
        : undefined;

      //check swagProducts
      const isSwagProduct =
        specs?.find((s) => s.code === SWAG_PRODUCT_CODE)?.specValue?.code ===
        SWAG_PRODUCT_VALUE_CODE;

      //manage location
      let locations: Location[] | undefined = undefined;

      if (item?.locations?.[0] && productHaveMainSection) {
        locations = item?.locations?.map((location: any) => {
          return {
            z: changeInchTo3dUnit(location?.left),
            x: changeInchTo3dUnit(location?.top),
            y: changeInchTo3dUnit(location?.height),
            rotation: location?.rotation ?? 0,
          };
        });
      }

      let model;
      if (prevVersion) {
        model = acc?.models?.find((model: TModel) => {
          const section = model.sections?.find((section) => section?.code === sectionCode);
          return itemModel?.id === model?.code && !section;
        });
      } else {
        model = acc?.models?.find((model: TModel) => {
          return model?.mainSectionId === itemParentId;
        });
      }

      if (model) {
        if (locations?.[0]) {
          model.location = locations?.[0];
        }

        model.sections = [
          ...model.sections,
          {
            ...newSection,
            id,
            code: sectionCode,
            modelId: model?.id,
            surfaces,
            isMain,
            isSemitransparent,
            isSwagProduct,
            brandTextures,
            sizeBrandTextures,
            hasBacklitSpec,
            isBacklit,
          },
        ];
        return {
          ...acc,
          ...surfaceIdsInstance,
        };
      } else {
        const modelKey = itemModel?.id;

        const newModel = {
          src: itemModel?._3DObject,
          id: modelId,
          mainSectionId: item?.id,
          code: modelKey,
          location: locations?.[0],
          isSizeSpecExist: Boolean(sizeSpec),
          dimensions: {
            length: changeInchTo3dUnit(itemModel?.dimensions?.length),
            width: changeInchTo3dUnit(itemModel?.dimensions?.width),
            height: changeInchTo3dUnit(itemModel?.dimensions?.height),
          },
          sections: [
            {
              ...newSection,
              id,
              modelId,
              code: sectionCode,
              surfaces,
              isMain,
              isSemitransparent,
              isSwagProduct,
              brandTextures,
              sizeBrandTextures,
              hasBacklitSpec,
              isBacklit,
            },
          ],
        };

        if (modelKey?.includes('FLOORING')) {
          return {
            models: [...acc.models, newModel],
            floorLength: changeInchTo3dUnit(itemModel?.dimensions?.length),
            floorWidth: changeInchTo3dUnit(itemModel?.dimensions?.width),
            ...surfaceIdsInstance,
          };
        } else {
          return {
            ...acc,
            models: [...acc.models, newModel],
            ...surfaceIdsInstance,
          };
        }
      }
    },
    Promise.resolve({
      models: [],
      floorWidth: 0,
      floorLength: 0,
      surfaceIds: [],
      itemsHaveBacklit: [],
    }),
  );
  return finalData;
};

export interface TextureSurface {
  surfaceCode: string;
  imageId: string;
  pathname?: string;
}
export interface ColorTheme {
  id: string;
  name: string;
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  accentColor: string;
}

export const initSpecs = async ({
  item,
  calcState,
  isApparel,
  colorTheme,
  patternSetSurfaces,
}: {
  item: {
    sku: string;
    product: Product;
    id: string;
    sizeSpecSections?: { specValueCode: string; section: Section }[];
    textures: { specValueCode: string; surfaces: TextureSurface[] }[];
  };
  calcState: any;
  isApparel: boolean;
  colorTheme: ColorTheme;
  patternSetSurfaces: Record<string, string>;
}) => {
  const sku = item?.sku?.split('-')?.map((item: string) => item?.split(':'));

  const specs = item?.product?.specs;
  const data = specs?.reduce(
    async (accPromise, spec) => {
      //init size spec
      let specValues = spec?.specsValue;
      const finalSpecs = await accPromise;
      if (spec?.code === SIZE_CODE) {
        specValues = (await Promise.all(
          spec?.specsValue?.map(async (sv) => {
            const section = item?.sizeSpecSections
              ? item?.sizeSpecSections?.find((item) => item?.specValueCode === sv?.code)?.section
              : sv?.section;
            const surfacesTexture = item?.textures?.find(
              (tex) => tex?.specValueCode === sv?.code,
            )?.surfaces;

            let shopCodeForSpecs: string;
            try {
              const themeStore = useThemeStore.getState();
              shopCodeForSpecs = themeStore.themeCode;
            } catch (e) {
              shopCodeForSpecs = 'ex'; // Default fallback
            }
            const { surfaceIds, surfaces } = await initSurfaces(
              item?.id,
              section?.surfaces as Surface[],
              false,
              false,
              shopCodeForSpecs,
              surfacesTexture,
              isApparel,
              colorTheme,
              patternSetSurfaces,
            );

            return {
              ...sv,
              section: {
                ...section,
                surfaceIds,
                surfaces,
                itemId: item?.id + '_2ND_' + section?.id,
              },
            };
          }),
        )) as SpecValue[];
      }

      //find selected spec value key

      let specValueCode: string | undefined;
      if (calcState) {
        const code = calcState?.products?.[item?.id]?.specs?.[spec?.code]?.valueCode;
        specValueCode = code;
      } else {
        const selectedSpec = sku?.find((skuItem: string[]) => skuItem?.[0] === spec?.code);
        specValueCode = selectedSpec?.[1]
          ? specValues?.find((specValue: SpecValue) => specValue?.code === selectedSpec?.[1])?.code
          : specValues?.[0]?.code;
      }

      if (specValueCode) {
        const specValue = specValues?.find(
          (specValueItem: SpecValue) => specValueItem?.code === specValueCode,
        );

        const newSpec = {
          ...spec,
          specsValue: specValues,
          specValue,
        };
        return [...finalSpecs, newSpec];
      } else {
        return finalSpecs;
      }
    },
    Promise.resolve([] as ProductSpec[]),
  );

  return data;
};

// Helper function to apply quality settings to textures
const applyTextureQualitySettings = (texture: THREE.Texture, isSvg = false) => {
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 16; // Maximum anisotropy for best quality
  if (isSvg) {
    texture.generateMipmaps = false; // Disable mipmaps for SVGs to maintain sharpness
    texture.flipY = false; // Ensure correct orientation for SVG textures
  }
  texture.needsUpdate = true;
};

export const loadTexture = (url: string): Promise<THREE.Texture> => {
  return new Promise((resolve, reject) => {
    const textureLoader = new THREE.TextureLoader();
    // const isSvg = url.toLowerCase().endsWith('.svg') || url.includes('image/svg+xml');
    textureLoader.load(
      url,
      (texture) => resolve(texture),
      undefined,
      (error) => reject(error),
    );
  });
};

export const loadTextureWithReplacedColors = async (
  url: string,
  colorMap: Record<string, string>, // e.g. { "#ff0000": "#00ff00", "#0000ff": "#ffff00" }
): Promise<THREE.Texture> => {
  // 1️⃣ Fetch the SVG source
  const response = await fetch(url);
  let svgText = await response.text();

  // 2️⃣ Replace color codes
  for (const [oldColor, newColor] of Object.entries(colorMap)) {
    const regex = new RegExp(oldColor.replace('#', '\\#'), 'gi');
    svgText = svgText.replace(regex, newColor);
  }

  // 3️⃣ Render SVG to ultra-high-resolution canvas for maximum quality
  const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
  const svgUrl = URL.createObjectURL(svgBlob);

  // Parse SVG to get dimensions (check viewBox, width, height attributes)
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
  const svgElement = svgDoc.documentElement;

  let baseWidth = 4096; // Ultra-high default resolution for maximum quality
  let baseHeight = 4096;

  // Try to get dimensions from SVG attributes
  const viewBox = svgElement.getAttribute('viewBox');
  if (viewBox) {
    const [, , w, h] = viewBox.split(/\s+|,/).map(parseFloat);
    if (w && h && w > 0 && h > 0) {
      baseWidth = w;
      baseHeight = h;
    }
  } else {
    const width = svgElement.getAttribute('width');
    const height = svgElement.getAttribute('height');
    if (width && height) {
      const parsedWidth = parseFloat(width.replace(/px|em|rem|pt/, '')) || 0;
      const parsedHeight = parseFloat(height.replace(/px|em|rem|pt/, '')) || 0;
      if (parsedWidth > 0 && parsedHeight > 0) {
        baseWidth = parsedWidth;
        baseHeight = parsedHeight;
      }
    }
  }

  // Load SVG as image to verify it loads correctly and get actual dimensions
  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    img.onload = () => {
      // Use natural dimensions if available and valid, otherwise use parsed dimensions
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        baseWidth = img.naturalWidth;
        baseHeight = img.naturalHeight;
      }
      resolve();
    };
    img.onerror = reject;
    img.src = svgUrl;
  });

  // Use ultra-high scale factor for maximum quality (4x minimum, up to 8x for very high DPI)
  const devicePixelRatio = window.devicePixelRatio || 1;
  const qualityScale = Math.max(devicePixelRatio * 2, 4); // Minimum 4x, scales with device pixel ratio

  // Cap maximum resolution to avoid memory issues (8192 is a common GPU limit)
  const maxDimension = 8192;
  const targetWidth = Math.min(baseWidth * qualityScale, maxDimension);
  const targetHeight = Math.min(baseHeight * qualityScale, maxDimension);

  // Calculate actual scale to use
  const actualScale = Math.min(targetWidth / baseWidth, targetHeight / baseHeight, qualityScale);

  // Create ultra-high-resolution canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', {
    alpha: true,
    desynchronized: false, // Better quality
    willReadFrequently: false,
  })!;

  // Set canvas size with ultra-high resolution
  canvas.width = baseWidth * actualScale;
  canvas.height = baseHeight * actualScale;

  // Enable high-quality image smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high'; // 'low' | 'medium' | 'high'

  // Scale context for high DPI
  ctx.scale(actualScale, actualScale);

  // Draw SVG to canvas with high quality
  ctx.drawImage(img, 0, 0, baseWidth, baseHeight);

  // 4️⃣ Create texture from ultra-high-resolution canvas
  const texture = new THREE.CanvasTexture(canvas);
  // Use high-quality settings optimized for SVG
  applyTextureQualitySettings(texture, true); // true because it's SVG-based

  // Cleanup
  URL.revokeObjectURL(svgUrl);

  return texture;
};

export const initSurfaces = async (
  itemKey: string,
  sectionSurfaces: Surface[],
  isSingleSide: boolean,
  isOutAsSingleSide: boolean,
  shopCode: string,
  surfacesTexture?: TextureSurface[],
  isApparel?: boolean,
  colorTheme?: ColorTheme,
  patternSetSurfaces?: Record<string, string>,
) => {
  const ids: string[] = [];
  const surfaces: Surface[] = [];

  const sectionSurfacesSorted = sectionSurfaces?.sort((a, b) => a.surfaceOrder - b.surfaceOrder);

  if (!Array.isArray(sectionSurfacesSorted)) {
    console.error('sectionSurfacesSorted is not iterable', sectionSurfacesSorted);
    return { surfaceIds: [], surfaces: [] };
  }

  for (const surface of sectionSurfacesSorted) {
    const surfaceTexture = surfacesTexture?.find((tex) => tex?.surfaceCode === surface?.code);

    const imageId = surfaceTexture?.pathname
      ? surfaceTexture?.imageId
      : isApparel
        ? patternSetSurfaces?.[surface?.facePatternTypeCode]
        : surfaceTexture?.imageId;

    const pathname =
      surfaceTexture?.pathname || (isApparel ? PATTERN_SETS : BUNDLE_PRODUCT_TEXTURES);

    let texture;
    let url;
    if (imageId) {
      try {
        const src =
          configService.getBaseUrl(shopCode).bucketStorage + '/' + pathname + '/' + imageId;
        url = src;
        texture =
          colorTheme && isApparel
            ? await loadTextureWithReplacedColors(src, {
                [PATTERN_SET_COLORS.PRIMARY]: colorTheme?.primaryColor,
                [PATTERN_SET_COLORS.SECONDARY]: colorTheme?.secondaryColor,
                [PATTERN_SET_COLORS.TERTIARY]: colorTheme?.tertiaryColor,
                [PATTERN_SET_COLORS.ACCENT]: colorTheme?.accentColor,
              })
            : await loadTexture(src);
      } catch (error) {
        console.error('Error loading texture', error);
      }
    }

    const id = surface?.id + '_2ND_' + itemKey;
    const newSurface = {
      id: id,
      code: surface?.code,
      surfaceOrder: surface?.surfaceOrder,
      texture,
      facePatternTypeCode: surface?.facePatternTypeCode,
      url,
      width: surface?.width,
      height: surface?.height,
    };
    ids.push(id);
    surfaces.push(newSurface);
  }

  return { surfaceIds: isSingleSide ? [ids?.[isOutAsSingleSide ? 1 : 0]] : ids, surfaces };
};

export const changeInchTo3dUnit = (value = 0) => {
  return value / 39.3701;
};

export const degreeTo3dRotationUnit = (rotation: number) => {
  return -((rotation * Math.PI) / 180);
};

export const calculate3dLocation = (finalData: TBundle) => {
  return finalData?.models?.map((model: TModel) => {
    const location = model?.location;
    const { boundsHeight, boundsWidth } = calculateBoundingWidthAndHeight({
      rotation: location?.rotation,
      width: model?.dimensions?.width,
      height: model?.dimensions?.length,
    });

    const threeDPosition = location && {
      z: finalData?.floorLength / 2 - boundsHeight / 2 - location?.z,
      x: -(finalData?.floorWidth / 2 - boundsWidth / 2 - location?.x),
      rotation: degreeTo3dRotationUnit(location?.rotation),
      y: location?.y,
    };

    return { ...model, location: threeDPosition };
  });
};

const calculateBoundingWidthAndHeight = ({
  rotation,
  width,
  height,
}: {
  rotation: number;
  width: number;
  height: number;
}) => {
  const rotationRad = rotation * (Math.PI / 180);

  // Calculate the half-width and half-height
  const halfWidth = width / 2;
  const halfHeight = height / 2;

  // Calculate the rotated corners of the rectangle
  const corners = [
    { x: -halfWidth, y: -halfHeight }, // Top-left
    { x: halfWidth, y: -halfHeight }, // Top-right
    { x: halfWidth, y: halfHeight }, // Bottom-right
    { x: -halfWidth, y: halfHeight }, // Bottom-left
  ].map(({ x, y }) => ({
    x: x * Math.cos(rotationRad) - y * Math.sin(rotationRad),
    y: x * Math.sin(rotationRad) + y * Math.cos(rotationRad),
  }));

  // Find the maximum and minimum x and y coordinates
  const minX = Math.min(...corners.map((corner) => corner.x));
  const maxX = Math.max(...corners.map((corner) => corner.x));
  const minY = Math.min(...corners.map((corner) => corner.y));
  const maxY = Math.max(...corners.map((corner) => corner.y));

  // Calculate the width and height of the bounding box
  const boundsWidth = maxX - minX;
  const boundsHeight = maxY - minY;

  return { boundsHeight, boundsWidth };
};

export const getPatternSetSurfaces = (
  items: { surfaces: { facePatternTypeCode: string; imageId: string }[] }[],
) => {
  if (!items) return {};
  const map: Record<string, string> = {};

  items.forEach((item) => {
    item.surfaces.forEach((surface) => {
      map[surface.facePatternTypeCode] = surface.imageId;
    });
  });

  return map;
};

async function createTextureWithSvg({
  jpgUrl,
  svgUrl,
  outWidth,
  outHeight,
  svgX,
  svgY,
  svgWidth,
  svgHeight,
  svgRotation = 0, // rotation in degrees
}: {
  jpgUrl: string;
  svgUrl: string;
  outWidth: number;
  outHeight: number;
  svgX: number;
  svgY: number;
  svgWidth: number;
  svgHeight: number;
  svgRotation?: number;
}) {
  // Load JPG
  const baseImg = new Image();
  baseImg.crossOrigin = 'anonymous';
  baseImg.src = jpgUrl;
  await new Promise((res) => (baseImg.onload = res));

  // Create output canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  canvas.width = outWidth;
  canvas.height = outHeight;

  // Draw scaled JPG
  ctx.drawImage(baseImg, 0, 0, outWidth, outHeight);

  // Load SVG
  const svgText = await fetch(svgUrl).then((r) => r.text());
  const blob = new Blob([svgText], { type: 'image/svg+xml' });
  const svgUrlObj = URL.createObjectURL(blob);

  const svgImg = new Image();
  svgImg.src = svgUrlObj;
  svgImg.crossOrigin = 'anonymous';
  await new Promise((res) => (svgImg.onload = res));

  URL.revokeObjectURL(svgUrlObj);

  // Convert degrees to radians
  const rad = (svgRotation * Math.PI) / 180;

  // ⭐ Rotate around the center of the SVG
  ctx.save();
  ctx.translate(svgX + svgWidth / 2, svgY + svgHeight / 2);
  ctx.rotate(rad);
  ctx.drawImage(svgImg, -svgWidth / 2, -svgHeight / 2, svgWidth, svgHeight);
  ctx.restore();

  // Create texture
  const textureUrl = canvas.toDataURL('image/png');
  return await loadTexture(textureUrl);
}

const changeInchToPX = (value?: number) => {
  return (value ?? 0) * 72;
};
